import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EllipsisIcon, Heart, MessageCircle, Send, ChevronLeft, ChevronRight, Bookmark, UserIcon } from 'lucide-react';
import { Player } from '@remotion/player';
import { Composition0002, calculateDurationFromProps } from '../remotion/Composition0002';

export function InstagramPreview({
  username,
  images,
  caption,
  profilePicture,
  skipImagePreload,
  isReel,
  inputProps,
}: {
  username: string;
  images: string[];
  caption: string;
  profilePicture?: string;
  skipImagePreload?: boolean;
  isReel?: boolean;
  inputProps?: Record<string, any>;
}) {
  const { t } = useTranslation('social');
  const [loadedImageIndices, setLoadedImageIndices] = useState<Set<number>>(new Set());
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [showFullCaption, setShowFullCaption] = useState(false);
  const playerRef = useRef<any>(null);

  useEffect(() => {
    // Reset states when images change
    setFailedImages(new Set());
    setCurrentImageIndex(0);

    if (images.length === 0 || skipImagePreload) {
      return;
    }

    // Preload all images
    const loadImage = (src: string, index: number) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          resolve();
        };

        img.onerror = () => {
          console.error(`InstagramPreview: Failed to load image ${index}`, src);
          setFailedImages((prev) => {
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
          });
          reject(new Error(`Failed to load image ${index}`));
        };

        img.src = src;
      });
    };

    // Load all images and set state when done
    Promise.allSettled(images.map((src, index) => loadImage(src, index))).then(() => {
      setLoadedImageIndices(new Set(images.map((_, index) => index)));
    });
  }, [images, skipImagePreload]);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => Math.min(images.length - 1, prev + 1));
  };

  const currentImageLoaded = skipImagePreload || loadedImageIndices.has(currentImageIndex);
  const allImagesLoaded = skipImagePreload || loadedImageIndices.size === images.length;

  const handleImageLoad = (index: number) => {
    if (!skipImagePreload) {
      setLoadedImageIndices((prev) => {
        const newSet = new Set(prev);
        newSet.add(index);
        return newSet;
      });
    }
  };

  // Show nothing while no images are available
  if (images.length === 0 && !isReel) {
    return null;
  }

  const renderCaption = () => {
    return (
      <div className="space-y-2 text-sm">
        <div
          className={`cursor-pointer ${!showFullCaption ? 'line-clamp-2' : ''}`}
          onClick={() => setShowFullCaption(!showFullCaption)}
        >
          <span className="font-semibold mr-2">{username}</span>
          {caption}
        </div>
        <div className="text-gray-500 text-xs">{t('instagram.minutes-ago')}</div>
      </div>
    );
  };

  // If this is a reel and we have inputProps, render the Remotion Player
  if (isReel && inputProps) {
    const { totalFrames } = calculateDurationFromProps(inputProps);
    const totalDurationInFrames = totalFrames;
    return (
      <div className="border rounded-md overflow-hidden bg-white max-w-[468px]">
        <div className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100">
              {profilePicture ? (
                <img src={profilePicture} alt={username} className="w-full h-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-gray-400" />
                </div>
              )}
            </div>
            <span className="font-semibold text-sm">{username}</span>
          </div>
          <EllipsisIcon className="w-5 h-5 text-gray-900" />
        </div>

        <div className="relative">
          <Player
            ref={playerRef}
            component={Composition0002}
            inputProps={inputProps}
            durationInFrames={totalDurationInFrames}
            compositionWidth={1080}
            compositionHeight={1920}
            fps={30}
            style={{
              width: '100%',
              aspectRatio: '9/16',
            }}
            controls={true}
            autoPlay
            loop
            initiallyMuted={true}
          />
        </div>

        <div className="p-3">
          <div className="flex justify-between py-3">
            <div className="flex gap-4">
              <Heart className="w-6 h-6" />
              <MessageCircle className="w-6 h-6" />
              <Send className="w-6 h-6" />
            </div>
            <Bookmark className="w-6 h-6" />
          </div>
          {renderCaption()}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border rounded-md overflow-hidden bg-white max-w-[468px] transition-opacity duration-300 ease-in-out ${
        allImagesLoaded ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100">
            {profilePicture ? (
              <img src={profilePicture} alt={username} className="w-full h-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>
          <span className="font-semibold text-sm">{username}</span>
        </div>
        <EllipsisIcon className="w-5 h-5 text-gray-900" />
      </div>

      <div className="relative">
        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
          >
            {images.map((image, index) => (
              <div key={index} className="w-full flex-shrink-0 relative">
                {failedImages.has(index) ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <span className="text-gray-400">{t('instagram.image-failed-to-load')}</span>
                  </div>
                ) : (
                  <div className="relative w-full overflow-hidden">
                    {!skipImagePreload && !currentImageLoaded && (
                      <div className="absolute inset-0 rounded bg-gray-200 animate-pulse" />
                    )}
                    <img
                      src={image}
                      alt={t('instagram.post-image-alt', { number: index + 1 })}
                      className="w-full object-contain max-h-[50vh]"
                      onLoad={() => handleImageLoad(index)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {images.length > 1 && (
            <div className="absolute top-4 right-4 bg-black/50 px-2 py-1 rounded-full text-white text-xs">
              {currentImageIndex + 1}/{images.length}
            </div>
          )}

          {images.length > 1 && (
            <>
              {currentImageIndex > 0 && (
                <button
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/40 rounded-full p-1 hover:opacity-80"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}
              {currentImageIndex < images.length - 1 && (
                <button
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/40 rounded-full p-1 hover:opacity-80"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="flex justify-center gap-1 mt-4">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  index === currentImageIndex ? 'bg-[#0095F6]' : 'bg-[#0095F6]/30'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="flex justify-between py-3">
          <div className="flex gap-4">
            <Heart className="w-6 h-6" />
            <MessageCircle className="w-6 h-6" />
            <Send className="w-6 h-6" />
          </div>
          <Bookmark className="w-6 h-6" />
        </div>
        {renderCaption()}
      </div>
    </div>
  );
}
